import { useState, useEffect } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { firestoreCache, CACHE_KEYS, CACHE_TTL } from '../utils/firestoreCache';
import { guardedGetDocs } from '../utils/guardedFirestore';

interface UseCourseEnrollmentCountResult {
  count: number;
  loading: boolean;
}

/**
 * 特定の授業の現在の履修人数を取得するフック。
 * コース詳細画面（CourseEvaluationScreen）などで全件取得フックの代わりに使用する。
 * 
 * キャッシュ: 5分間メモリキャッシュ。
 */
export const useCourseEnrollmentCount = (courseId: string | undefined): UseCourseEnrollmentCountResult => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchCount = async () => {
      if (!courseId) {
        setCount(0);
        setLoading(false);
        return;
      }

      const cacheKey = CACHE_KEYS.courseEnrollmentCount(courseId);
      const cached = firestoreCache.get<number>(cacheKey);
      if (cached !== null) {
        if (!cancelled) {
          setCount(cached);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        const enrollmentsRef = collection(db, 'enrollments');
        const q = query(enrollmentsRef, where('courseId', '==', courseId));
        const snap = await guardedGetDocs(q);

        if (!snap) {
          if (!cancelled) setLoading(false);
          return;
        }

        const size = snap.size;
        firestoreCache.set(cacheKey, size, CACHE_TTL.COURSE_ENROLLMENT_COUNT);

        if (!cancelled) {
          setCount(size);
        }
      } catch (error) {
        console.warn(`[useCourseEnrollmentCount] Failed to fetch count for course ${courseId}:`, error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCount();

    return () => {
      cancelled = true;
    };
  }, [courseId]);

  return { count, loading };
};
